import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getStoredToken } from '../services/auth';

interface BlogPost {
  id: number;
  title: string;
  slug: string;
  excerpt: string;
  author: string;
  is_published: boolean;
  published_date: string | null;
  created_at: string;
  tags: string[];
}

const AdminBlog: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isBlogger } = useAuth();
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    if (!isBlogger) {
      navigate('/');
      return;
    }
    fetchPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isBlogger, navigate]);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const token = getStoredToken();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      // Fetch all posts including drafts
      const response = await fetch(`${API_URL}/api/blog/posts?published_only=false&limit=100`, {
        headers,
      });

      if (!response.ok) {
        throw new Error('Failed to fetch posts');
      }

      const data = await response.json();
      setPosts(data.posts);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load posts');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (slug: string, title: string) => {
    if (!window.confirm(`Are you sure you want to delete "${title}"?`)) {
      return;
    }

    try {
      const token = getStoredToken();
      const headers: Record<string, string> = {};

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_URL}/api/blog/posts/${slug}`, {
        method: 'DELETE',
        headers,
      });

      if (!response.ok) {
        throw new Error('Failed to delete post');
      }

      // Refresh the list
      fetchPosts();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete post');
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Not published';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return <div className="loading">Loading posts...</div>;
  }

  return (
    <div className="admin-container">
      <div className="page-header">
        <h1 className="page-title">Manage Blog Posts</h1>
        <Link to="/admin/blog/new" className="btn btn-primary">
          Create New Post
        </Link>
      </div>

      {error && <div className="error">{error}</div>}

      {posts.length === 0 ? (
        <div className="admin-section">
          <p>No blog posts yet. Create your first post to get started!</p>
          <Link to="/admin/blog/new" className="btn btn-primary">
            Create First Post
          </Link>
        </div>
      ) : (
        <div className="admin-section">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Status</th>
                <th>Published</th>
                <th>Tags</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {posts.map((post) => (
                <tr key={post.id}>
                  <td>
                    <strong>{post.title}</strong>
                    {post.excerpt && (
                      <div style={{ fontSize: '0.875rem', color: '#666', marginTop: '0.25rem' }}>
                        {post.excerpt.substring(0, 100)}
                        {post.excerpt.length > 100 ? '...' : ''}
                      </div>
                    )}
                  </td>
                  <td>
                    <span
                      className={`status-badge ${post.is_published ? 'published' : 'draft'}`}
                      style={{
                        padding: '0.25rem 0.75rem',
                        borderRadius: '4px',
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        backgroundColor: post.is_published ? '#d4edda' : '#fff3cd',
                        color: post.is_published ? '#155724' : '#856404',
                      }}
                    >
                      {post.is_published ? 'Published' : 'Draft'}
                    </span>
                  </td>
                  <td>{formatDate(post.published_date)}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                      {post.tags.slice(0, 3).map((tag) => (
                        <span key={tag} className="blog-tag" style={{ fontSize: '0.75rem' }}>
                          {tag}
                        </span>
                      ))}
                      {post.tags.length > 3 && (
                        <span style={{ fontSize: '0.75rem', color: '#666' }}>
                          +{post.tags.length - 3}
                        </span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      {post.is_published && (
                        <Link
                          to={`/blog/${post.slug}`}
                          className="btn-link"
                          style={{ fontSize: '0.875rem' }}
                          target="_blank"
                        >
                          View
                        </Link>
                      )}
                      <Link
                        to={`/admin/blog/edit/${post.slug}`}
                        className="btn-link"
                        style={{ fontSize: '0.875rem' }}
                      >
                        Edit
                      </Link>
                      <button
                        onClick={() => handleDelete(post.slug, post.title)}
                        className="btn-link"
                        style={{
                          fontSize: '0.875rem',
                          color: '#dc3545',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: 0,
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminBlog;
